import { useMemo } from 'react'
import { KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { usePreferences } from '@/shell/stores/preferences'
import type { FreightPipeline, PipelineStage } from '../api/types'
import { BOARD_STAGES, STAGE_SHORT_LABELS, STAGE_VARIANT, transitionAllowed } from '../utils/pipeline-stage-meta'

const MODULE_ID = 'logistics-pipeline'

interface PipelineKanbanItem extends KanbanItem {
  pipeline: FreightPipeline
}

export interface FreightPipelineKanbanProps {
  items: FreightPipeline[]
  selectedId?: string
  onSelect: (pipeline: FreightPipeline) => void
  onTransition: (pipeline: FreightPipeline, target: PipelineStage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  // KanbanStage's accent doesn't have an `info` slot — fold info → neutral.
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<PipelineStage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<PipelineStage, KanbanStage>,
)

function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as PipelineStage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

/**
 * Logistics overhaul Phase 2a — kanban view of the freight_pipeline table.
 *
 * Drag a card to a target column to dispatch a transition;
 * `transitionAllowed()` gates which column-pairs accept the drop
 * (server-side enforcement is identical, including the tender-permission
 * guard on moves into "Tendered").
 */
export function FreightPipelineKanban({
  items,
  selectedId,
  onSelect,
  onTransition,
}: FreightPipelineKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: PipelineKanbanItem[] = useMemo(
    () =>
      items.map((pipeline) => ({
        id: String(pipeline.id),
        stageId: pipeline.stage,
        pipeline,
      })),
    [items],
  )

  return (
    <KanbanBoard<PipelineKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) =>
        transitionAllowed(from as PipelineStage, to as PipelineStage)
      }
      onAdvance={(item, _from, to) => onTransition(item.pipeline, to as PipelineStage)}
      onSelect={(item) => onSelect(item.pipeline)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <PipelineKanbanCard pipeline={item.pipeline} />}
      scrollRestorationKey="logistics"
    />
  )
}

function PipelineKanbanCard({ pipeline }: { pipeline: FreightPipeline }) {
  return (
    <div className="flex flex-col gap-1.5">
      <a
        href={odooUrl('purchase.order', pipeline.odoo_po_id)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-role-data-primary truncate text-accent-text hover:underline"
      >
        {pipeline.odoo_po_name}
      </a>
      {pipeline.requested_provider ? (
        <div className="text-role-data-secondary truncate">
          {pipeline.requested_provider}
        </div>
      ) : (
        <div className="text-role-metadata italic text-tertiary">No carrier chosen yet</div>
      )}
      {pipeline.quote_options && pipeline.quote_options.length > 0 ? (
        <div className="text-role-metadata">
          {pipeline.quote_options.length} option{pipeline.quote_options.length === 1 ? '' : 's'}{' '}
          compared
        </div>
      ) : null}
      <div className="text-role-metadata text-tertiary">
        Updated {formatDate(pipeline.updated_at)}
      </div>
    </div>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
